import { z } from 'zod';
import { addressSchema, blockTagSchema, evmTopicSchema, genericAddressSchema, hexDataSchema, evmChainSlugSchema, supportedChainSlugSchema, txHashSchema, } from './common.js';
/** Use to request logs with address and topic filters. */
export const getLogsParamsSchema = z.object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema.optional(),
    topic0: evmTopicSchema.optional(),
    cursor: blockTagSchema.optional(),
    fromBlock: blockTagSchema.optional(),
    toBlock: blockTagSchema.optional(),
}).strict();
/** Use to request transfers for one address. */
export const getTransfersParamsSchema = z.object({
    chain: supportedChainSlugSchema,
    address: addressSchema,
    cursor: blockTagSchema.optional(),
    fromBlock: blockTagSchema.optional(),
    toBlock: blockTagSchema.optional(),
}).strict();
/** Use to request one token allowance. */
export const getTokenAllowanceParamsSchema = z.object({
    chain: supportedChainSlugSchema,
    address: addressSchema,
    owner: addressSchema,
    spender: addressSchema,
    blockHeight: z.number().int().nonnegative().optional(),
}).strict();
/** One log entry in a logs response. */
export const logEntrySchema = z.object({
    address: addressSchema,
    topics: z.array(evmTopicSchema),
    data: hexDataSchema,
    blockNumber: z.number().int().nonnegative(),
    transactionHash: txHashSchema,
    transactionIndex: z.string().min(1),
    blockHash: txHashSchema,
    logIndex: z.number().int().nonnegative(),
    removed: z.boolean(),
    // Many upstreams (geth >= 1.13) include a timestamp per log entry; keep optional
    // so fixtures captured against either generation of node pass validation.
    blockTimestamp: z.string().min(1).optional(),
    // Legacy field emitted by some Ethereum clients (parity/erigon) alongside logIndex.
    transactionLogIndex: z.string().min(1).optional(),
}).strict();
/** Response shape for log queries (EVM). */
const evmLogsDataSchema = z.object({
    chain: supportedChainSlugSchema,
    count: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
    cursor: z.union([z.number().int().nonnegative(), z.string().min(1), z.null()]),
    logs: z.array(logEntrySchema),
}).strict();
/** Response shape for log queries (Starknet — raw getEvents envelope). */
const starknetLogsDataSchema = z.object({
    chain: supportedChainSlugSchema,
    address: genericAddressSchema.optional(),
    fromBlock: z.number().int().nonnegative().optional(),
    toBlock: z.number().int().nonnegative().optional(),
    events: z.unknown(),
}).catchall(z.unknown());
export const logsDataSchema = z.union([evmLogsDataSchema, starknetLogsDataSchema]);
/** One transfer entry in a transfers response. */
export const transferEntrySchema = z.object({
    from: addressSchema,
    to: addressSchema,
    value: z.string().min(1),
    token: addressSchema.optional(),
    blockNumber: z.number().int().nonnegative(),
    transactionHash: txHashSchema,
}).strict();
/** Response shape for transfer queries. */
export const transfersDataSchema = z.object({
    chain: evmChainSlugSchema,
    address: addressSchema,
    count: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    hasMore: z.boolean(),
    cursor: z.union([z.number().int().nonnegative(), z.string().min(1), z.null()]),
    transfers: z.array(transferEntrySchema),
}).strict();
/** Response shape for token allowance lookups. */
export const tokenAllowanceDataSchema = z.object({
    chain: evmChainSlugSchema,
    token: addressSchema,
    symbol: z.union([z.string().min(1), z.null()]),
    owner: addressSchema,
    spender: addressSchema,
    allowance: z.string().min(1),
    allowanceRaw: z.string().min(1),
    isUnlimited: z.boolean(),
}).strict();
